import { Observable, BehaviorSubject } from 'rxjs';
import { Component, ViewChildren, ViewChild, AfterViewInit, QueryList, ChangeDetectorRef, Injectable, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { StateService } from '../shared/state-management/state.service';
import { LoadingPanelService, LoadingPanelSettings } from '../shared/loading-panel/loading-panel.service';
import { DistributionService } from './services/distribution.service';
import { DistributionExportService } from './services/distribution-export.service';
import { DataFieldGroup } from '../shared/datafields/models/datafield-group';
import { DataFieldValue } from '../shared/datafields/models/datafield-value';
import { DataFieldGroupContainerComponent } from '../shared/datafields/datafield-group-container.component';
import { GenericModalService, ModalSettingsMessage, ModalSettingsJsonViewer, ModalSettingsTemplate } from '../shared/generic-modal/generic-modal.service';
import { Distribution } from './models/distribution';
import { DistributionFallbackDirection } from './models/distribution-fallback-direction';
import { DocumentTypeEntity } from './models/distribution-object';
import { FilenamingType } from './models/filenaming-type';
import { Direction } from './models/direction';
import { Clone } from '../shared/utils';
import { DistributionItineraryComponent } from './distribution-itinerary/distribution-itinerary.component';
import { DuplicateCheckService } from '../tracking/services/duplicate-check.service';
import { DuplicateCheckTypeEntity } from '../tracking/models/duplicate-check-type';
import { DuplicateCheckConfigSaveObject } from './models/distribution-itinerary-config';
import { acknowledgementOptions } from './models/acknowledgementOption';
import { NgClass, AsyncPipe } from '@angular/common';
import { BatchingSelectorComponent } from '../distribution/batching-selector/batching-selector.component';
import { AlertConfiguration } from '../alerts/models/alert-configuration';
import { ScheduleItem } from '../shared/scheduling/models';
import { AlertService } from '../alerts/services/alert.service';
import { AlertConfigurationHistoryDialogComponent } from '../alerts/components/alert-configuration-history-dialog/alert-configuration-history-dialog.component';
import { AlertConfigurationEditComponent } from '../alerts/components/alert-configuration-edit/alert-configuration-edit.component';
import { TrackingSharedActionMethodsService } from '../tracking/services/tracking-shared-action-methods.service';
import { ConfigurationService } from '../configuration/services/configuration.service';
import { CurrentUserService } from '../user/services';
import { DistributionCreateNewComponent } from './distribution-create-new.component';
import { SvgIconComponent } from '../shared/icons/svg-icon.component';
import { PermissionRequiredDirective } from '../shared/authentication/permission-required.directive';
import { ChangeLogLinkComponent } from '../shared/change-log/change-log-link/change-log-link.component';
import { ChangeLogLinkEntityDirective } from '../shared/change-log/change-log-link-entity/change-log-link-entity.directive';
import { FormsModule, UntypedFormGroup } from '@angular/forms';
import { PartnerSelectorComponent } from '../partner/partner-selector.component';
import { TransportConfigurationSelectorComponent } from '../transport/transport-configuration-selector.component/transport-configuration-selector.component';
import { DocumentConfigSelectorSimpleComponent } from './document-config-selector-simple.component';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { BatchingSelectorComponent as BatchingSelectorComponent_1 } from './batching-selector/batching-selector.component';
import { ApplicationInsightsService } from '../shared/insights/application-insights.service';
import { InitConfiguration } from './models/init-configuration';
import { RestrictedSearchCalloutComponent, RestrictedSearchCalloutType } from '../shared/restricted-search/restricted-search.component';
import { ContextPropertyFiltersComponent } from './distribution-filter/context-property-filters/context-property-filters.component';
import { ContextPropertyFilterDto } from './models/context-property-filter-dto';

type DistributionRouteParams = Params & {
    distributionIds?: string;
    createNew?: string | boolean;
    fallback?: string | boolean;
};

type ExtendedDuplicateCheckType = DuplicateCheckTypeEntity & {
    isSelected?: boolean;
    duplicateCheckConfig?: DataFieldGroup[];
};

@Component({
    templateUrl: './distribution-search-result-details.component.html',
    styleUrls: ['./distribution-search-result-details.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [DistributionCreateNewComponent, RestrictedSearchCalloutComponent, SvgIconComponent, PermissionRequiredDirective, ChangeLogLinkComponent, ChangeLogLinkEntityDirective, FormsModule, DataFieldGroupContainerComponent, PartnerSelectorComponent, TransportConfigurationSelectorComponent, DocumentConfigSelectorSimpleComponent, DistributionItineraryComponent, AlertConfigurationEditComponent, NgClass, NgbTooltip, BatchingSelectorComponent_1, ContextPropertyFiltersComponent, AsyncPipe]
})
export class DistributionSearchResultDetailsComponent implements AfterViewInit {
    private readonly appInsights = inject(ApplicationInsightsService);
    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly distributionService = inject(DistributionService);
    private readonly modalService = inject(GenericModalService);
    private readonly duplicateCheckService = inject(DuplicateCheckService);
    private readonly exportService = inject(DistributionExportService);
    private readonly cdRef = inject(ChangeDetectorRef);
    private readonly loadingPanelService = inject(LoadingPanelService);
    private readonly stateService = inject(StateService);
    private readonly alertService = inject(AlertService);
    private readonly trackingService = inject(TrackingSharedActionMethodsService);
    private readonly configurationService = inject(ConfigurationService);
    private readonly currentUserService = inject(CurrentUserService);

    readonly acknowledgementOptions = acknowledgementOptions;
    readonly directionEnum = Direction; // Used in html template
    readonly selectedDistribution = signal<Distribution | null>(null);
    readonly propertyFilters = signal<ContextPropertyFilterDto[]>([]);

    readonly RestrictedSearchCalloutType = RestrictedSearchCalloutType;

    readonly validationFormGroup: UntypedFormGroup = new UntypedFormGroup({});
    isCreatingNew = false;
    isEditing = false;
    parms: DistributionRouteParams | null = null;
    distributions: Distribution[] = [];
    distributionIds: number[] = [];
    selectedDistributionOriginal: Distribution | null;
    selectedDistributionObject: DocumentTypeEntity | null;

    isFallback = false;
    isShowPartnerIn = true;
    isShowPartnerOut = true;
    showAlert = false;

    @ViewChildren('configurationKeysDistributionContainer')
    configurationKeyDataFieldGroupContainers: QueryList<DataFieldGroupContainerComponent>;

    @ViewChildren('duplicateCheckAdvancedObjectContainer')
    duplicateCheckAdvancedObjectContainer: QueryList<DataFieldGroupContainerComponent>;

    @ViewChild('distributionItineraryOut')
    distributionItineraryOut: DistributionItineraryComponent;

    @ViewChild('distributionItineraryIn')
    distributionItineraryIn: DistributionItineraryComponent;

    @ViewChild('alertConfiguration')
    alertConfiguration: AlertConfigurationEditComponent;


    @ViewChild('batchingSelector', { static: false })
    batchingSelector: BatchingSelectorComponent;

    readonly filenamingTypes = new BehaviorSubject<FilenamingType[]>([]);
    duplicateCheckTypes: ExtendedDuplicateCheckType[] = [];

    // Configuration Keys
    readonly distributionConfigurationKeys = new BehaviorSubject<DataFieldGroup[]>([]);
    readonly distributionAdvancedConfigurationKeys = new BehaviorSubject<DataFieldGroup[]>([]);
    readonly distributionObjectConfigurationKeys = new BehaviorSubject<DataFieldGroup[]>([]);
    readonly partnerFromObjectConfigurationKeys = new BehaviorSubject<DataFieldGroup[]>([]);
    readonly partnerToObjectConfigurationKeys = new BehaviorSubject<DataFieldGroup[]>([]);
    readonly documentConfigFromObjectConfigurationKeys = new BehaviorSubject<DataFieldGroup[]>([]);
    readonly documentConfigToObjectConfigurationKeys = new BehaviorSubject<DataFieldGroup[]>([]);
    readonly formatTypeFromObjectConfigurationKeys = new BehaviorSubject<DataFieldGroup[]>([]);
    readonly formatTypeToObjectConfigurationKeys = new BehaviorSubject<DataFieldGroup[]>([]);

    // Configuration Keys Constants
    private readonly usageKeyDistributionObjectSpecific = 'Distribution_DistributionObject';
    private readonly usageKeyDistributionPartner = 'Distribution_Partner';
    private readonly usageKeyDistributionDocumentConfig = 'Distribution_DocumentConfig';
    private readonly usageKeyDistributionFormatType = 'Distribution_FormatType';
    private readonly usageKeyDistributionGeneral = 'Distribution_General';
    private readonly usageKeyDistributionAdvanced = 'Distribution_Advanced';

    ngAfterViewInit(): void {
        this.route.params.subscribe((parms: DistributionRouteParams) => {
            this.parms = parms;
            if (parms.distributionIds) {
                this.distributionIds = parms.distributionIds.split(',').map(Number);

                // Make sure we don't load more documents than allowed...
                if (this.distributionIds.length > DistributionService.maxNumberOfDistributionsToShow) {
                    this.distributionIds = this.distributionIds.slice(0, DistributionService.maxNumberOfDistributionsToShow);
                }

                this.refresh();
            }
            else if (parms.createNew) {
                this.refresh();
                setTimeout(() => {
                    this.isFallback = parms.fallback ? true : false;
                    this.createNew();
                });
            }
        });
    }

    refresh(selectedDistributionId?: number): void {
        this.loadingPanelService.showLoadingPanel(new LoadingPanelSettings('Loading distributions...'));
        this.distributionService.getDistributions(this.distributionIds).then((result) => {
            this.distributions = result;
            if (result.length > 0) {

                let distributionToSelect: Distribution | undefined;
                if (selectedDistributionId) {
                    distributionToSelect = this.distributions.find((d) => d.DistributionsId === selectedDistributionId);
                }
                else if (!this.selectedDistribution()) {
                    distributionToSelect = result[0];
                }
                else {
                    distributionToSelect = this.distributions.find((d) => d.DistributionsId === this.selectedDistribution().DistributionsId);
                }
                this.selectedDistributionChanged(distributionToSelect, true);
            }
            else {
                this.selectedDistributionChanged(undefined, true);
            }
            this.loadingPanelService.hideLoadingPanel();
        }).catch(() => this.loadingPanelService.hideLoadingPanel());

        this.showAlert = this.currentUserService.hasPermission('Alert_Edit_Configuration');
    }

    /**
     * Tell the search-result page to reload data.
     */
    private refreshSearchData(): void {
        const stateObject: unknown = this.stateService.getState('tracking-search-result-state');
        if (stateObject) {
            (stateObject as { refreshData?: boolean; }).refreshData = true;
        }
    }

    setTransportConfigurationOut(transportConfiguration: Distribution['TransportConfigurationOut']): void {
        this.selectedDistribution.update(current => {
            if (!current) return current;

            return { ...current, TransportConfigurationOut: transportConfiguration } as Distribution;
        });
    }

    //
    // EDIT
    //
    edit(): void {
        this.isEditing = true;
    }

    //
    // SAVE
    //
    save(): void {

        //Validate required fields
        if (!this.validate()) {
            return;
        }

        this.isEditing = false;
        this.loadingPanelService.showLoadingPanelDirect('Saving distribution...');

        let configKeys: DataFieldValue[] = [];
        // We need to include empty values - otherwise it will not be possible to clear a field...
        this.configurationKeyDataFieldGroupContainers.forEach(groupContainer => configKeys = configKeys.concat(groupContainer.getDataFieldValues(true)));

        const isNew = !this.selectedDistribution().DistributionsId;
        this.appInsights.logEvent('DistributionEdit', { 'event': 'save', isNew });

        const duplicateCheckConfigSaveObject = this.buildDuplicateCheckConfigSaveObject();
        this.selectedDistribution.update(current => {
            if (!current) return current;

            return {
                ...current,
                BatchingConfigurationsId: this.batchingSelector?.selectedBatchingConfiguration?.Id || null,
                DocumentConfigOutBatchersId: this.batchingSelector?.selectedConfigOutBatcher?.Id || null,
                ContextPropertyFilters: this.normalizeContextPropertyFilters(this.propertyFilters()),
            } as Distribution;
        });

        this.distributionService.saveDistribution(this.selectedDistribution(), configKeys,
            this.distributionItineraryIn.allValues, this.distributionItineraryOut.allValues, duplicateCheckConfigSaveObject)
            .then(savedDistribution => {
                Clone.extend(true, this.selectedDistributionOriginal, savedDistribution);

                if (isNew) {
                    this.distributionIds.push(this.selectedDistributionOriginal.DistributionsId);
                    this.selectedDistribution.set(Clone.extend(true, {}, savedDistribution) as Distribution);
                }

                this.refreshSearchData();
                this.loadingPanelService.hideLoadingPanel();

                // Refresh to ensure that password fields read updated secretId's.
                this.refresh();
            })
            .catch(() => {
                this.refresh();
            });
    }

    private buildDuplicateCheckConfigSaveObject(): DuplicateCheckConfigSaveObject[] {
        const selectedDuplicateCheckTypes = this.duplicateCheckTypes.filter(dit => dit.isSelected === true);
        const selectedDuplicateCheckDataGroupContainers = this.duplicateCheckAdvancedObjectContainer.filter(container => selectedDuplicateCheckTypes.some(dpc => dpc.Id === container.referenceId));

        const result: DuplicateCheckConfigSaveObject[] = [];

        selectedDuplicateCheckTypes.forEach(x => {
            const selectedDuplicateCheckDataGroupContainer = selectedDuplicateCheckDataGroupContainers.find(z => z.referenceId === x.Id);
            const duplicateCheckConfigSaveObject = {} as DuplicateCheckConfigSaveObject;
            duplicateCheckConfigSaveObject.DuplicateCheckTypeId = x.Id;
            duplicateCheckConfigSaveObject.DataFieldValues = selectedDuplicateCheckDataGroupContainer?.getDataFieldValues(true);
            result.push(duplicateCheckConfigSaveObject);
        });

        return result;
    }

    private normalizeContextPropertyFilters(filters: ContextPropertyFilterDto[] | null | undefined): ContextPropertyFilterDto[] {
        return (filters ?? []).map(filter => ({
            ...filter,
            Id: filter.Id && filter.Id > 0 ? filter.Id : null,
            Values: (filter.Values ?? []).map(value => ({
                ...value,
                Id: value.Id && value.Id > 0 ? value.Id : null,
            })),
        }));
    }

    private validate(): boolean {
        const missingFields: string[] = [];
        const distribution = this.selectedDistribution();
        const isFallbackDistribution = this.distributionService.isFallbackDistribution(distribution);
        const fallbackDirection = this.distributionService.fallbackDirection(distribution);

        if (this.validationFormGroup.invalid) {
            missingFields.push('- Configuration keys');
        }

        const isPartnerInRequired = !isFallbackDistribution || fallbackDirection !== DistributionFallbackDirection.In;
        if (!distribution.PartnerIn && isPartnerInRequired) {
            missingFields.push('- From Partner');
        }

        const isPartnerOutRequired = !isFallbackDistribution || fallbackDirection !== DistributionFallbackDirection.Out;
        if (!distribution.PartnerOut && isPartnerOutRequired) {
            missingFields.push('- To Partner');
        }

        if (!this.selectedDistribution().TransportConfigurationOut) {
            missingFields.push('- To Partner Location');
        }

        if (!this.selectedDistribution().DocumentConfigIn || !this.selectedDistribution().DocumentConfigIn.DocumentConfigsId) {
            missingFields.push('- From Format');
        }

        if (!this.selectedDistribution().DocumentConfigOut || !this.selectedDistribution().DocumentConfigOut.DocumentConfigsId) {
            missingFields.push('- To Format');
        }

        if (this.selectedDistribution().ExpectAcknowledgement && !this.isValidAcknowledgementTimeout(this.selectedDistribution().AcknowledgementTimeout)) {
            missingFields.push('- Timeout should be a number and greater than 0');
        }

        if (this.selectedDistribution().DuplicateInterchangeCheck && this.selectedDistribution().DaysToKeepDoubletCheckRecords !== null && this.selectedDistribution().DaysToKeepDoubletCheckRecords !== undefined) {
            if (isNaN(this.selectedDistribution().DaysToKeepDoubletCheckRecords)) {
                missingFields.push('- Days to keep duplicate check records requires a number');
            }
            else if (this.selectedDistribution().DaysToKeepDoubletCheckRecords < 1) {
                missingFields.push('- Days to keep duplicate check records requires a number greater than 0');
            }
        }

        //Distributions cannot be allowed to use proxy partners
        if (this.selectedDistribution().PartnerIn?.IsProxyPartner === true) {
            missingFields.push('- From Partner cannot be proxy partner');
        }
        if (this.selectedDistribution().PartnerOut?.IsProxyPartner === true) {
            missingFields.push('- To Partner cannot be proxy partner');
        }

        if (!this.distributionItineraryIn.isValid) {
            missingFields.push('- From Itinerary (missing configuration)');
        }

        if (!this.distributionItineraryOut.isValid) {
            missingFields.push('- To Itinerary (missing configuration)');
        }

        //batching configuration
        if (this.batchingSelector !== undefined) {
            if (this.batchingSelector.selectedConfigOutBatcher !== null && this.batchingSelector.selectedBatchingConfiguration === null) {
                missingFields.push('- Batching configuration (must include both batcher and release configuration)');
            }
        }

        if (this.alertConfiguration !== undefined && this.alertConfiguration !== null && !this.alertConfiguration.isScheduleValid) {
            missingFields.push('- AlertConfiguration (Invalid schedule)');
        }

        if (this.alertConfiguration !== undefined && this.alertConfiguration !== null && this.alertConfiguration.alertConfiguration().RequiredNumberOfDocuments < 1) {
            missingFields.push('- AlertConfiguration (Number of documents must be a positive number');
        }

        const propertyFilters = this.propertyFilters();
        const hasInvalidPropertyFilter = propertyFilters.some(filter => {
            const hasPropertyName = !!filter.PropertyName?.trim();
            const hasNamespace = !!filter.PropertyNamespace?.trim();
            const hasValues = (filter.Values ?? []).length > 0;
            const allValuesFilled = (filter.Values ?? []).every(v => !!v.Value?.trim());

            return !hasPropertyName || !hasNamespace || !hasValues || !allValuesFilled;
        });

        if (hasInvalidPropertyFilter) {
            missingFields.push('- Context Property Filters (each filter requires a property name, namespace and value)');
        }

        if (missingFields.length > 0) {
            const settings = new ModalSettingsMessage('Required fields', 'The following fields are invalid:<br>' + missingFields.join('<br>'));
            this.modalService.message(settings).catch(() => { });
            return false;
        }

        return true;
    }

    private isValidAcknowledgementTimeout(value: number | null | undefined): boolean {
        if (isNaN(value) || value < 1) {
            return false;
        }
        return true;
    }

    resetDefaultValue(): void {
        //if checkbox is uncheck and user enter an invalid value we need to reset it to the detault
        if (!this.selectedDistribution().ExpectAcknowledgement) {
            if (isNaN(this.selectedDistribution().AcknowledgementTimeout) || this.selectedDistribution().AcknowledgementTimeout < 1) {
                //set default value
                this.selectedDistribution.update(current => {
                    if (!current) return current;
                    return { ...current, AcknowledgementTimeout: 30 } as Distribution;
                });
            }
        }
    }
    //
    // CANCEL
    //
    cancel(ignoreEmpty = false): void {
        this.isEditing = false;

        this.appInsights.logEvent('DistributionEdit', { 'event': 'cancel', 'isNew': !this.selectedDistribution().DistributionsId, 'DistributionsId': this.selectedDistribution().DistributionsId });

        if (!this.selectedDistribution().DistributionsId) { // If cancelling a new distribution just remove it from the array and select the distribution at index zero.
            this.removeDistribution(this.selectedDistribution());
        }

        this.selectedDistributionChanged(this.selectedDistributionOriginal);
        if (!ignoreEmpty) {
            this.checkIfAny();
        }
    }

    private removeDistribution(_distribution: Distribution): void {
        // REVIEW: This will only work as long as the user only can create one distribution at a time.
        const index = this.distributions.findIndex((d) => d.DistributionsId === this.selectedDistribution().DistributionsId);
        if (index >= 0) {
            this.distributions.splice(index, 1);
            if (this.distributions.length > 0) {
                this.selectedDistributionOriginal = this.distributions[0];
            }
            else {
                this.selectedDistributionOriginal = null;
            }
        }
    }

    removeDistributionClick(distribution: Distribution): void {
        const index = this.distributionIds.indexOf(distribution.DistributionsId);
        if (index >= 0) {
            this.distributionIds.splice(index, 1);
        }

        void this.router.navigate(['distribution/result/details', { distributionIds: this.distributionIds.join(',') }]);
        this.refresh();
    }

    //
    // DELETE
    //
    delete(): void {
        if (this.selectedDistribution()) {
            this.modalService.confirmDirect('Delete distribution', 'Are you sure you want to delete the selected distribution?').then(() => {
                if (!this.selectedDistribution().DistributionsId) { // If it's a new distribuion - just remove it
                    this.removeDistribution(this.selectedDistribution());
                    this.selectedDistributionChanged(this.selectedDistributionOriginal);
                    this.checkIfAny();
                }
                else { // Otherwise delete it in the database
                    void this.distributionService.deleteDistribution(this.selectedDistribution().DistributionsId).then(() => {
                        this.removeDistribution(this.selectedDistribution());
                        this.selectedDistributionChanged(this.selectedDistributionOriginal);
                        this.refreshSearchData();
                        this.checkIfAny();
                    });
                }
            }).catch(() => { });
        }
    }

    private checkIfAny(): void {
        if (!this.selectedDistributionOriginal) {
            void this.router.navigate(['distribution']);
        }
    }

    //
    // CREATE NEW
    //
    createNew(): void {
        if (this.selectedDistribution() && !this.selectedDistribution().DistributionsId) {
            this.cancel(true); // If we are in the process of creating a new distribution and select "Create new" againg (and CanDeActivate returns true) - just cancel the current creation.
        }
        this.selectedDistribution.set(null);
        this.selectedDistributionObject = null;
        this.selectedDistributionOriginal = null;
        this.isCreatingNew = true;
    }

    newDistributionSaved(distribution: Distribution): void {
        this.isCreatingNew = false;
        this.distributions.push(distribution);
        this.selectedDistributionChanged(distribution, true, true);
        void this.batchingSelector.refresh();

        // Refresh page to get the correct URL and remove the createNew parameter. Notice: this 'refresh' doesn't reinitialize the component, but just trigger the route.params subscription!
        // this.router.navigate(['distribution/details', { distributionIds: this.distributionIds.join(',') }]);
    }

    newDistributionCancelled(): void {
        this.isCreatingNew = false;
    }


    //
    // EXPORT
    //
    async export() {
        if (this.selectedDistribution() && this.selectedDistribution().DistributionsId) {
            this.loadingPanelService.showLoadingPanelDirect('Exporting distributions...');

            const includeArtifacts = await this.includeArtifactsInExport();
            await this.exportService.exportDistributions([this.selectedDistribution().DistributionsId], includeArtifacts);
            this.loadingPanelService.hideLoadingPanel();
        }
    }

    //
    // DISTRIBUTION CONFIGURATION KEYS
    //

    private findDistributionConfigurationKeys(): Promise<void> {
        return this.findConfigurationKeys(this.usageKeyDistributionGeneral, this.distributionConfigurationKeys);
    }

    //
    // DISTRIBUTION CONFIGURATION KEYS
    //

    private findDistributionAdvancedConfigurationKeys(): Promise<void> {
        return this.findConfigurationKeys(this.usageKeyDistributionAdvanced, this.distributionAdvancedConfigurationKeys);
    }

    //
    // DISTRIBUTION-OBJECT CONFIGURATION KEYS
    //

    private async findDistributionObjectConfigurationKeys(): Promise<void> {
        if (this.selectedDistribution() && this.selectedDistribution().DocumentConfigIn) {
            await this.findConfigurationKeys(this.usageKeyDistributionObjectSpecific, this.distributionObjectConfigurationKeys, this.selectedDistributionObject?.Id);
        }
        else {
            this.distributionObjectConfigurationKeys.next([]);
        }
    }

    //
    // PARTNER CONFIGURATION KEYS
    //

    invokeFindPartnerConfigurationKeys(direction?: Direction): Promise<unknown> {
        return this.findPartnerConfigurationKeys(direction).catch(() => { });
    }

    private async findPartnerConfigurationKeys(direction?: Direction): Promise<void> {
        let inPromise: Promise<void> | undefined;
        let outPromise: Promise<void> | undefined;

        ////Distributions cannot be allowed to use proxy partners
        //if (this.selectedDistribution().PartnerIn != null && this.selectedDistribution().PartnerIn.IsProxyPartner == true) {
        //    this.selectedDistribution().PartnerIn = undefined;
        //    this.cdRef.detectChanges();
        //    alert("From Partner: Cannot select Proxy Partners!");
        //}
        //else if (this.selectedDistribution().PartnerOut != null && this.selectedDistribution().PartnerOut.IsProxyPartner == true) {
        //    this.selectedDistribution().PartnerOut = undefined;
        //    this.cdRef.detectChanges();
        //    alert("To Partner: Cannot select Proxy Partners!");
        //}

        if ((direction === undefined || direction === Direction.In)) {
            if (this.selectedDistribution().PartnerIn) {
                inPromise = this.findConfigurationKeys(this.usageKeyDistributionPartner, this.partnerFromObjectConfigurationKeys, this.selectedDistribution().PartnerIn.PartnersId);
            }
            else {
                this.partnerFromObjectConfigurationKeys.next([]);
            }
        }
        if ((direction === undefined || direction === Direction.Out)) {
            if (this.selectedDistribution().PartnerOut) {
                outPromise = this.findConfigurationKeys(this.usageKeyDistributionPartner, this.partnerToObjectConfigurationKeys, this.selectedDistribution().PartnerOut.PartnersId);
            }
            else {
                this.partnerToObjectConfigurationKeys.next([]);
            }
        }

        // Promise.all accepts plain values too, so an undefined branch resolves immediately.
        await Promise.all([inPromise, outPromise]);
    }

    //
    // DOCUMENT CONFIG CONFIGURATION KEYS
    //

    // This method is used from the HTML when document-config has changed
    invokeFindDocumentConfigConfigurationKeys(direction?: Direction): Promise<unknown> {
        return this.findDocumentConfigConfigurationKeys(direction).catch(() => { });
    }

    private async findDocumentConfigConfigurationKeys(direction?: Direction): Promise<void> {
        let inPromise: Promise<void> | undefined;
        let outPromise: Promise<void> | undefined;

        if ((direction === undefined || direction === Direction.In)) {
            if (this.selectedDistribution().DocumentConfigIn && this.selectedDistribution().DocumentConfigIn.DocumentConfigsId) {
                inPromise = this.findConfigurationKeys(
                    this.usageKeyDistributionDocumentConfig,
                    this.documentConfigFromObjectConfigurationKeys,
                    this.selectedDistribution().DocumentConfigIn.DocumentConfigsId);
            }
            else {
                this.documentConfigFromObjectConfigurationKeys.next([]);
            }
        }
        if ((direction === undefined || direction === Direction.Out)) {
            if (this.selectedDistribution().DocumentConfigOut && this.selectedDistribution().DocumentConfigOut.DocumentConfigsId) {
                outPromise = this.findConfigurationKeys(
                    this.usageKeyDistributionDocumentConfig,
                    this.documentConfigToObjectConfigurationKeys,
                    this.selectedDistribution().DocumentConfigOut.DocumentConfigsId);
            }
            else {
                this.documentConfigToObjectConfigurationKeys.next([]);
            }
        }

        // Promise.all accepts plain values too, so an undefined branch resolves immediately.
        await Promise.all([inPromise, outPromise]);
    }

    //
    // FORMAT TYPES CONFIGURATION KEYS
    //

    // This method is used from the HTML when format-type has changed
    invokeFindFormatTypeConfigurationKeys(direction: Direction): Promise<unknown> {
        return this.findFormatTypeConfigurationKeys(direction).catch(() => { });
    }

    private async findFormatTypeConfigurationKeys(direction?: Direction): Promise<void> {
        let inPromise: Promise<void> | undefined;
        let outPromise: Promise<void> | undefined;

        if (direction === undefined || direction === Direction.In) {
            if (this.selectedDistribution().DocumentConfigIn && this.selectedDistribution().DocumentConfigIn.Format) {
                inPromise = this.findConfigurationKeys(
                    this.usageKeyDistributionFormatType,
                    this.formatTypeFromObjectConfigurationKeys,
                    this.selectedDistribution().DocumentConfigIn.Format.FormatTypesId);
            }
            else {
                this.formatTypeFromObjectConfigurationKeys.next([]);
            }
        }
        if (direction === undefined || direction === Direction.Out) {
            if (this.selectedDistribution().DocumentConfigOut && this.selectedDistribution().DocumentConfigOut.Format) {
                outPromise = this.findConfigurationKeys(
                    this.usageKeyDistributionFormatType,
                    this.formatTypeToObjectConfigurationKeys,
                    this.selectedDistribution().DocumentConfigOut.Format.FormatTypesId);
            }
            else {
                this.formatTypeToObjectConfigurationKeys.next([]);
            }
        }

        // Promise.all accepts plain values too, so an undefined branch resolves immediately.
        await Promise.all([inPromise, outPromise]);
    }

    //
    // FILENAMING TYPES
    //

    private findFilenamingTypes(): Promise<void> {
        if (this.selectedDistributionObject) {
            return this.distributionService.getFilenamingTypes(this.selectedDistributionObject.Id).then((filenamingTypes) => {
                this.filenamingTypes.next(filenamingTypes);
            });
        }
        else {
            return Promise.resolve();
        }
    }

    //
    // DUPLICATE CHECK
    //

    private async findDuplicateCheckTypes(): Promise<void> {
        const duplicateCheckTypes = await this.duplicateCheckService.getDuplicateCheckTypes();
        this.duplicateCheckTypes = await Promise.all(duplicateCheckTypes.map(async dct => {
            const existingDuplicateCheck = this.selectedDistribution().EnabledDuplicateChecks.find(d => d.DuplicateCheckTypeId === dct.Id);
            const duplicateCheckConfig = await this.duplicateCheckService.getDuplicateCheckDataFieldGroups(dct.ArtifactId, existingDuplicateCheck?.DuplicateCheckConfigurationId);
            return { ...dct, duplicateCheckConfig, isSelected: !!existingDuplicateCheck };
        }));
    }

    private clearSelectedDuplicateChecks(): void {
        if (this.duplicateCheckTypes) {
            this.duplicateCheckTypes.forEach(dit => dit.isSelected = false);
        }
    }

    //
    // PROPERTY CONDITIONS
    //

    onPropertyFiltersChange(updatedFilters: ContextPropertyFilterDto[]): void {
        this.propertyFilters.set(updatedFilters);
    }

    //
    // Alerts
    //

    get currentHasAlertConfiguration(): boolean {
        const result = !!this.selectedDistribution()?.AlertConfigurationPresentation;
        return result;
    }

    addAlert(): void {
        const newScehdule = new ScheduleItem();
        const newDate = new Date();
        newDate.setHours(0, 0, 0, 0);
        newScehdule.StartDate = newDate;
        newScehdule.Enabled = true;

        const existingAlertConfiguration = this.distributions.find((d) => d.DistributionsId === this.selectedDistribution().DistributionsId)?.AlertConfigurationPresentation;
        const newAlertConfigurationId = existingAlertConfiguration?.Id ?? 0;

        const newAlertConfiguration = {
            Id: newAlertConfigurationId,
            DistributionId: this.selectedDistribution().DistributionsId,
            IsEnabled: true,
            StakeholderId: 0,
            NotifyStakeholder: false,
            IsUrgent: false,
            OverrideMailTemplate: false,
            OverrideMailSubject: null,
            OverrideMailBody: null,
            ScheduleItem: newScehdule,
            RequiredNumberOfDocuments: 1,
        } as AlertConfiguration;
        this.updateAlertItem(newAlertConfiguration);
    }

    removeAlert(): void {
        this.updateAlertItem(null);
    }

    async cancelAlert(): Promise<void> {
        if (this.selectedDistribution().AlertConfigurationPresentation?.Id === null || this.selectedDistribution().AlertConfigurationPresentation?.Id === undefined) {
            return;
        }

        const id = Number(this.selectedDistribution().AlertConfigurationPresentation.Id);

        const requestText = 'Do you want to cancel alerts for this distribution for the rest of the day?';

        await this.modalService.confirmDirect('Cancel Alert', requestText).then(async () => {
            const selectedAlertConfigurationIds = [id];
            await this.alertService.cancelAlerts(selectedAlertConfigurationIds);
        }).catch(() => { });
    }

    async showAlertHistory(alertConfigurationId: number): Promise<void> {
        const alertEvents = await this.alertService.getAlertEventsForAlertConfiguration(alertConfigurationId);
        const componentData = { alertEvents };

        try {
            await this.modalService.openComponent(AlertConfigurationHistoryDialogComponent, componentData, new ModalSettingsTemplate('lg', 'static'));
        }
        catch {
            // Ignore error as it will be thrown if the user closes the modal by clicking outside of it or pressing escape, which is expected behavior
        }
    }

    private updateAlertItem(alertConfiguration: AlertConfiguration | null): void {
        if (this.selectedDistribution()) {
            this.selectedDistribution.update(current => {
                if (!current) return current;
                return { ...current, AlertConfigurationPresentation: alertConfiguration } as Distribution;
            });
        }
    }

    private async loadAllExtra(): Promise<void> {
        await Promise.all([
            this.findDistributionConfigurationKeys(),
            this.findDistributionAdvancedConfigurationKeys(),
            this.findDistributionObjectConfigurationKeys(),
            this.findPartnerConfigurationKeys(),
            this.findDocumentConfigConfigurationKeys(),
            this.findFormatTypeConfigurationKeys(),
            this.findFilenamingTypes(),
            this.findDuplicateCheckTypes()
        ]);
    }

    private findConfigurationKeys(keyUsage: string, target: BehaviorSubject<DataFieldGroup[]>, foreignKey?: number): Promise<void> {
        return this.distributionService.getDistributionConfigurations(keyUsage, this.selectedDistribution().DistributionsId, foreignKey).then((result) => {
            if (result.length > 0) {
                const group = new DataFieldGroup();
                group.DataFields = result;
                target.next([group]);
            }
            else {
                target.next([]);
            }
        });
    }

    selectedDistributionChanged(distribution: Distribution | null | undefined, _detectChanges: boolean = false, enableEdit: boolean = false): void {
        if (distribution) {

            this.selectedDistributionOriginal = distribution;
            this.selectedDistribution.set(Clone.extend(true, {}, distribution) as Distribution);
            this.propertyFilters.set(this.normalizeContextPropertyFilters(distribution.ContextPropertyFilters));

            if ((this.selectedDistribution()?.DistributionsId ?? 0) <= 0) {
                if (this.isFallback) {
                    this.isShowPartnerIn = false;
                    this.isShowPartnerOut = true;
                }
                else {
                    this.isShowPartnerIn = true;
                    this.isShowPartnerOut = true;
                }
            }
            else if (this.distributionService.isFallbackDistribution(this.selectedDistribution())) {
                this.isShowPartnerIn = this.distributionService.fallbackDirection(this.selectedDistribution()) !== DistributionFallbackDirection.In;
                this.isShowPartnerOut = this.distributionService.fallbackDirection(this.selectedDistribution()) !== DistributionFallbackDirection.Out;
            }
            else {
                this.isShowPartnerIn = true;
                this.isShowPartnerOut = true;
            }

            this.loadingPanelService.showLoadingPanelDirect('Loading configuration keys...');
            this.selectedDistributionObject = this.selectedDistribution().DocumentConfigIn.DistributionObject;

            //Temp fix of issue with document-config-selector-simple.component on default distribution (partner null)
            this.cdRef.detectChanges();

            void this.loadAllExtra().then(() => {
                this.loadingPanelService.hideLoadingPanel();
                if (enableEdit) {
                    this.edit();
                }
                this.cdRef.detectChanges();
            });

            this.selectedDistribution.update(current => {
                if (!current) return current;

                return {
                    ...current,
                    AcknowledgementTimeout: current.AcknowledgementTimeout || 30,
                } as Distribution;
            });
        }
        else {
            this.selectedDistribution.set(null);
            this.propertyFilters.set([]);
        }
    }

    viewRawDistribution(): void {
        if (this.selectedDistribution()) {
            //let documentConfigInPromise = this.distributionService
            //    .getItineraryStepParameters(this.selectedDistribution.DocumentConfigIn.ItineraryStepString,
            //        this.selectedDistribution.DistributionsId).then(
            //            (result) => {
            //                this.selectedDistribution.DocumentConfigIn['ItineraryStepParameters'] = result;
            //            });

            //let documentConfigOutPromise = this.distributionService
            //    .getItineraryStepParameters(this.selectedDistribution.DocumentConfigOut.ItineraryStepString,
            //        this.selectedDistribution.DistributionsId).then(
            //            (result) => {
            //                this.selectedDistribution.DocumentConfigOut['ItineraryStepParameters'] = result;
            //            });

            // TODO: Vi kan vel ikke vise itinerary på denne måde længere?


            //observableForkJoin(documentConfigInPromise, documentConfigOutPromise).subscribe(() => {
            const settings =
                new ModalSettingsJsonViewer('Raw distribution details', null, this.selectedDistribution());
            settings.backdrop = 'static';
            settings.windowClass = 'hugeModal';
            void this.modalService.jsonViewer(settings);
            //}
            //);
        }
    }

    private showText(header: string, text: string): void {
        text = this.escapeHtml(text);
        this.modalService.messageDirect(header, '<pre>' + text + '</pre>').catch(() => { });
    }

    private readonly entityMap: Record<string, string> = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        '\'': '&#39;',
        '/': '&#x2F;',
    };

    private escapeHtml(val: string): string {
        return String(val).replace(/[&<>"'\\/]/g, s => this.entityMap[s]);
    }

    //
    // UPLOAD TEST FILE
    //
    async uploadTestFile() {
        if ((this.selectedDistribution()) && this.selectedDistribution().DistributionsId) {
            const distributionIds: number[] = [this.selectedDistribution().DistributionsId];

            const initConfig = {
                FormatTypeId: null,
                DistributionIds: distributionIds
            } as InitConfiguration;

            await this.trackingService.uploadDocument('Configure the uploaded document', initConfig);
        }
    }

    private justLeave = false;
    canDeactivate(): Promise<boolean> | boolean {
        if (!this.justLeave && (this.isEditing || this.isCreatingNew)) {
            return this.modalService.confirmDirect('Not saved', 'You have not saved your changes. Continue?').then(() => { this.justLeave = true; return true; }).catch(() => false);
        }
        return true;
    }

    private async includeArtifactsInExport(): Promise<boolean> {
        const hasExportArtifactsPermission = this.currentUserService.hasPermission('ImportExport_Action_ExportArtifacts');
        let defaultIncludeArtifacts = true;
        await this.configurationService.getEffectiveSettingBoolean('IncludeArtifactsInExport').then(res => defaultIncludeArtifacts = res ?? true);
        return defaultIncludeArtifacts && hasExportArtifactsPermission;
    }

    isFallbackDistribution(distribution: Distribution): boolean {
        if ((this.selectedDistribution()?.DistributionsId ?? 0) <= 0) {
            return this.isFallback;
        }
        return this.distributionService.isFallbackDistribution(distribution);
    }

    fallbackDistributionDirection(distribution: Distribution): string {
        const direction = this.distributionService.fallbackDirection(distribution);
        return direction ? direction.toString() : '';
    }
}


@Injectable({
    providedIn: 'root'
})
export class DistributionSearchResultDetailsCanDeactivate {
    canDeactivate(component: DistributionSearchResultDetailsComponent): Observable<boolean> | Promise<boolean> | boolean {
        return component.canDeactivate();
    }
}
